import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import WorkspacesRoundedIcon from '@mui/icons-material/WorkspacesRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import { useApp } from '../context/AppContext';
import { ViralShareCard } from '../components/ViralShareCard';
import {
  assignMailboxWorkspace,
  createNudgeWorkspace,
  deleteNudgeWorkspace,
  disconnectEnrichment,
  disconnectNudgeMailbox,
  renameNudgeWorkspace,
  saveEnrichmentConnection,
  saveMailboxNudgeSettings,
  saveNudgeSettings,
  saveResearchSettings,
  setNudgeStatus,
  testEnrichmentConnection,
  inviteOrganizationMember,
  updateOrganizationMember,
  removeOrganizationMember,
  resendOrganizationInvite,
} from '../services/appStateService';
import { UpgradeModal } from '../components/UpgradeModal';

const notificationOptions = [
  { key: 'scanResults', label: 'New missed opportunities', note: 'Email me when Nudge finds conversations worth reviewing. Rate-limited to about once per day.' },
  { key: 'weeklyDigest', label: 'Weekly recap', note: 'A weekly summary of scans, opportunities, drafts, sent follow-ups, and replies.' },
  { key: 'referralUpdates', label: 'Referral and bonus credits', note: 'Credit rewards and referral activation updates.' },
  { key: 'creditAlerts', label: 'Low-credit alerts', note: 'Email me when draft credits are running low or exhausted.' },
  { key: 'mailboxIssues', label: 'Mailbox reconnect alerts', note: 'Token, permission, or mailbox connection problems that need action.' },
  { key: 'productUpdates', label: 'Product updates', note: 'Occasional product updates and launch announcements.' },
];

const lookbackOptions = [7, 30, 60, 90, 180, 365, 730, 1460];
const cadenceOptions = {
  gentle: { label: 'Gentle', note: '5–7 business days', minDays: 5, maxDays: 7 },
  balanced: { label: 'Balanced', note: '3–5 business days', minDays: 3, maxDays: 5 },
  persistent: { label: 'Persistent', note: '2–3 business days', minDays: 2, maxDays: 3 },
};

const providerMeta = {
  cleanlist: {
    label: 'Cleanlist',
    short: 'Structured enrichment',
    description: 'Find person and company details such as role, company, industry, firmographics, and other B2B context.',
    placeholder: 'Cleanlist API key',
  },
  apollo: {
    label: 'Apollo',
    short: 'People + company enrichment',
    description: 'Match the recipient and company, then add professional and organization context when Apollo has it.',
    placeholder: 'Apollo API key',
  },
  tavily: {
    label: 'Web research',
    short: 'Recent public signals · powered by Tavily',
    description: 'Search the public web for recent launches, funding, partnerships, hiring, and other timely context that can make a follow-up meaningfully more specific.',
    placeholder: 'Tavily API key',
  },
};

const sectionGroups = [
  {
    label: 'Organization',
    requiresAdmin: true,
    items: [
      { id: 'organization', label: 'Overview', icon: <BusinessRoundedIcon /> },
      { id: 'all-mailboxes', label: 'All mailboxes', icon: <MailOutlineRoundedIcon /> },
      { id: 'members', label: 'Team members', icon: <PeopleAltRoundedIcon /> },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { id: 'workspace', label: 'General', icon: <WorkspacesRoundedIcon /> },
      { id: 'mailboxes', label: 'Mailboxes', icon: <MailOutlineRoundedIcon /> },
    ],
  },
  {
    label: 'Follow-ups',
    items: [
      { id: 'automation', label: 'Automation', icon: <TuneRoundedIcon /> },
      { id: 'drafting', label: 'Writing & content', icon: <DraftsOutlinedIcon /> },
      { id: 'safety', label: 'Safety', icon: <SecurityRoundedIcon /> },
    ],
  },
  {
    label: 'Research',
    items: [
      { id: 'enrichment', label: 'Enrichment', icon: <AutoAwesomeRoundedIcon /> },
    ],
  },
  {
    label: 'Account',
    requiresOwner: true,
    items: [
      { id: 'notifications', label: 'Notifications', icon: <NotificationsNoneRoundedIcon /> },
      { id: 'credits', label: 'Credits & referrals', icon: <RedeemRoundedIcon /> },
    ],
  },
];

const validSections = new Set(sectionGroups.flatMap((group) => group.items.map((item) => item.id)));

function maxLookbackForSubscription(limits = {}) {
  return Math.max(30, Number(limits.lookbackDays || 0), Number(limits.historicalLookbackDays || 0));
}

function lookbackLabel(days) {
  if (days === 365) return '1 year';
  if (days === 730) return '2 years';
  if (days === 1460) return '4 years';
  if (days === 90) return '3 months';
  if (days === 180) return '6 months';
  return `${days} days`;
}

function providerLabel(provider = '') {
  if (provider === 'google') return 'Gmail';
  if (provider === 'microsoft') return 'Outlook';
  if (provider === 'yahoo') return 'Yahoo';
  if (provider === 'icloud') return 'iCloud';
  return 'IMAP';
}

function SettingRow({ title, note, children, last = false }) {
  return (
    <Box sx={{ py: 2.2, borderBottom: last ? 0 : '1px solid rgba(26,29,49,.08)' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2.2}>
        <Box sx={{ minWidth: 0, maxWidth: 600 }}>
          <Typography fontWeight={850}>{title}</Typography>
          {note && <Typography variant="body2" color="text.secondary" sx={{ mt: .45, lineHeight: 1.55 }}>{note}</Typography>}
        </Box>
        <Box sx={{ flex: '0 0 auto', minWidth: { sm: 180 } }}>{children}</Box>
      </Stack>
    </Box>
  );
}

function SectionSurface({ title, description, action, children }) {
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(26,29,49,.09)', borderRadius: 2.5, overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} alignItems={{ sm: 'center' }} sx={{ px: { xs: 2.2, sm: 3 }, py: 2.4, bgcolor: 'rgba(26,29,49,.018)', borderBottom: '1px solid rgba(26,29,49,.08)' }}>
        <Box>
          <Typography variant="h3" sx={{ fontSize: 23 }}>{title}</Typography>
          {description && <Typography color="text.secondary" variant="body2" sx={{ mt: .45, maxWidth: 700 }}>{description}</Typography>}
        </Box>
        {action}
      </Stack>
      <Box sx={{ px: { xs: 2.2, sm: 3 }, py: .4 }}>{children}</Box>
    </Box>
  );
}

function SettingsNav({ section, onChange, canManageOrganization, canManageBilling }) {
  return (
    <Box sx={{ position: 'sticky', top: 92 }}>
      {sectionGroups.filter((group) => (!group.requiresAdmin || canManageOrganization) && (!group.requiresOwner || canManageBilling)).map((group) => (
        <Box key={group.label} sx={{ mb: 2.3 }}>
          <Typography variant="caption" fontWeight={900} color="text.secondary" sx={{ px: 1.2, textTransform: 'uppercase', letterSpacing: '.08em' }}>{group.label}</Typography>
          <Stack sx={{ mt: .65 }}>
            {group.items.map((item) => {
              const active = item.id === section;
              return (
                <Button
                  key={item.id}
                  color="inherit"
                  startIcon={item.icon}
                  onClick={() => onChange(item.id)}
                  sx={{
                    justifyContent: 'flex-start',
                    px: 1.2,
                    py: .85,
                    borderRadius: 1.3,
                    fontWeight: active ? 900 : 700,
                    color: active ? '#101228' : 'text.secondary',
                    bgcolor: active ? 'rgba(255,182,72,.14)' : 'transparent',
                    '&:hover': { bgcolor: active ? 'rgba(255,182,72,.18)' : 'rgba(26,29,49,.045)' },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

export function SettingsPage() {
  const { state, update, updateSettings, refreshAppState, setActiveWorkspace } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const requestedSection = new URLSearchParams(location.search).get('section') || 'workspace';
  const canManageOrganization = Boolean(state.access?.canManageOrganization);
  const canManageBilling = Boolean(state.access?.canManageBilling);
  const adminOnlySections = new Set(['organization', 'all-mailboxes', 'members']);
  const ownerOnlySections = new Set(['notifications', 'credits']);
  const section = validSections.has(requestedSection)
    && (!adminOnlySections.has(requestedSection) || canManageOrganization)
    && (!ownerOnlySections.has(requestedSection) || canManageBilling)
    ? requestedSection
    : 'workspace';

  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [upgradeReason, setUpgradeReason] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceBusy, setWorkspaceBusy] = useState('');
  const [selectedMailboxId, setSelectedMailboxId] = useState('');
  const [mailboxUseWorkspaceDefaults, setMailboxUseWorkspaceDefaults] = useState(true);
  const [mailboxSettingsDraft, setMailboxSettingsDraft] = useState({});
  const [targetInput, setTargetInput] = useState('');
  const [blockedInput, setBlockedInput] = useState('');
  const [allowedInput, setAllowedInput] = useState('');
  const [providerKeys, setProviderKeys] = useState({ cleanlist: '', apollo: '', tavily: '' });
  const [providerBusy, setProviderBusy] = useState('');
  const [providerMessage, setProviderMessage] = useState('');
  const [researchSettingsDraft, setResearchSettingsDraft] = useState({ providerOrder: ['cleanlist', 'apollo', 'tavily'], strategy: 'waterfall', stopWhenEnough: true, minimumQualityScore: 4 });
  const [memberBusy, setMemberBusy] = useState('');
  const [memberMessage, setMemberMessage] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('workspace_manager');
  const [inviteWorkspaceIds, setInviteWorkspaceIds] = useState([]);
  const [memberDrafts, setMemberDrafts] = useState({});

  const s = state.settings;
  const limits = state.subscription?.limits || {};
  const maxLookbackDays = maxLookbackForSubscription(limits);
  const canRemoveBranding = Boolean(limits.removeBranding || limits.brandingToggleAvailable);
  const canAutoCreateDrafts = Boolean(limits.autoCreateDrafts);
  const canUseAttachments = Boolean(limits.includeThreadAttachments);
  const canUseCustomInstructions = Boolean(limits.customInstructions);
  const canAutoSend = Boolean(limits.autoSend);
  const canResearchAndDraft = Boolean(limits.researchAndDraft);
  const canUseCustomCadence = Boolean(limits.customCadence);
  const maxFollowUps = Math.max(1, Number(limits.maxFollowUps || 10));
  const canContinueUntilReply = Boolean(limits.allowContinueUntilReply);
  const activeWorkspaceId = state.activeWorkspaceId || state.defaultWorkspaceId || '';
  const activeWorkspace = (state.workspaces || []).find((workspace) => workspace.id === activeWorkspaceId) || null;
  const workspaceLimit = Math.max(1, Number(limits.workspaces || 1));
  const teamMemberLimit = Math.max(1, Number(limits.teamMembers || 1));
  const unlimitedWorkspaces = Boolean(limits.unlimitedWorkspaces);
  const unlimitedTeamMembers = Boolean(limits.unlimitedTeamMembers);
  const workspaceLimitLabel = unlimitedWorkspaces ? 'Unlimited' : workspaceLimit.toLocaleString();
  const teamMemberLimitLabel = unlimitedTeamMembers ? 'Unlimited' : teamMemberLimit.toLocaleString();
  const canManageTeam = Boolean(limits.teamManagement) && canManageOrganization;
  const organizationWorkspaces = state.allWorkspaces?.length ? state.allWorkspaces : state.workspaces || [];
  const organizationMailboxes = useMemo(() => (state.allMailboxes || state.mailboxes || []).filter((mailbox) => mailbox.status !== 'disconnected' && mailbox.health !== 'disconnected'), [state.allMailboxes, state.mailboxes]);
  const allMailboxes = useMemo(() => (state.mailboxes || []).filter((mailbox) => mailbox.status !== 'disconnected' && mailbox.health !== 'disconnected'), [state.mailboxes]);
  const workspaceMailboxes = useMemo(() => allMailboxes.filter((mailbox) => mailbox.workspaceId === activeWorkspaceId), [allMailboxes, activeWorkspaceId]);
  const selectedMailbox = workspaceMailboxes.find((mailbox) => (mailbox.mailboxId || mailbox.id) === selectedMailboxId) || null;
  const teamMembers = Array.isArray(state.organization?.team) ? state.organization.team : [];
  const allowedEnrichmentProviders = Array.isArray(limits.allowedEnrichmentProviders) && limits.allowedEnrichmentProviders.length
    ? limits.allowedEnrichmentProviders
    : ['cleanlist', 'apollo', 'tavily'];
  const analytics = state.analytics || {};
  const creditsTotal = Number(analytics.creditsTotal || 0);
  const creditsUsed = Number(analytics.creditsUsed || 0);
  const creditsRemaining = Math.max(0, Number(analytics.creditsRemaining ?? (creditsTotal - creditsUsed)));
  const creditPct = creditsTotal > 0 ? Math.min(100, Math.round((creditsUsed / creditsTotal) * 100)) : 0;

  useEffect(() => {
    const first = workspaceMailboxes[0];
    const nextId = first?.mailboxId || first?.id || '';
    if (!workspaceMailboxes.some((mailbox) => (mailbox.mailboxId || mailbox.id) === selectedMailboxId)) setSelectedMailboxId(nextId);
  }, [activeWorkspaceId, workspaceMailboxes, selectedMailboxId]);

  useEffect(() => {
    if (!selectedMailbox) {
      setMailboxUseWorkspaceDefaults(true);
      setMailboxSettingsDraft({ ...state.settings });
      return;
    }
    const custom = selectedMailbox.settingsMode === 'custom';
    setMailboxUseWorkspaceDefaults(!custom);
    setMailboxSettingsDraft({ ...state.settings, ...(custom ? selectedMailbox.customSettings || {} : {}) });
  }, [selectedMailboxId, selectedMailbox?.settingsMode, activeWorkspaceId]);

  useEffect(() => {
    const next = activeWorkspace?.researchSettings || { providerOrder: ['cleanlist', 'apollo', 'tavily'], strategy: 'waterfall', stopWhenEnough: true, minimumQualityScore: 4 };
    setResearchSettingsDraft({
      providerOrder: Array.isArray(next.providerOrder) ? next.providerOrder : ['cleanlist', 'apollo', 'tavily'],
      strategy: next.strategy === 'all' ? 'all' : 'waterfall',
      stopWhenEnough: next.stopWhenEnough !== false,
      minimumQualityScore: Number(next.minimumQualityScore || 4),
    });
    setProviderMessage('');
    setProviderKeys({ cleanlist: '', apollo: '', tavily: '' });
  }, [activeWorkspaceId]);


  useEffect(() => {
    const next = {};
    for (const member of teamMembers) {
      next[member.id || member.memberId] = {
        role: member.role || 'workspace_manager',
        workspaceIds: Array.isArray(member.workspaceIds) ? member.workspaceIds : [],
      };
    }
    setMemberDrafts(next);
  }, [state.organization?.team]);

  const goSection = (next) => navigate(`/settings?section=${next}`);
  const openUpgrade = (reason) => setUpgradeReason(reason);

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      await saveNudgeSettings({ settings: state.settings, mailboxes: workspaceMailboxes, activate: state.active, workspaceId: activeWorkspaceId });
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
      await refreshAppState();
    } catch (err) {
      setError(err?.message || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  const createWorkspace = async () => {
    const name = workspaceName.trim();
    if (!name) return;
    setWorkspaceBusy('create'); setError('');
    try {
      const result = await createNudgeWorkspace({ name, copyFromWorkspaceId: activeWorkspaceId });
      setWorkspaceName('');
      await refreshAppState();
      if (result?.workspace?.id) setActiveWorkspace(result.workspace.id);
    } catch (err) { setError(err?.message || 'Could not create workspace.'); }
    finally { setWorkspaceBusy(''); }
  };

  const renameWorkspace = async (workspace = activeWorkspace) => {
    if (!workspace) return;
    const nextName = window.prompt('Workspace name', workspace.name || 'Workspace');
    if (!nextName?.trim()) return;
    setWorkspaceBusy('rename'); setError('');
    try { await renameNudgeWorkspace({ workspaceId: workspace.id, name: nextName.trim() }); await refreshAppState(); }
    catch (err) { setError(err?.message || 'Could not rename workspace.'); }
    finally { setWorkspaceBusy(''); }
  };

  const removeWorkspace = async (workspace = activeWorkspace) => {
    if (!workspace || workspace.isDefault) return;
    if (!window.confirm(`Delete “${workspace.name}”? Move or disconnect its mailboxes first.`)) return;
    setWorkspaceBusy('delete'); setError('');
    try {
      await deleteNudgeWorkspace({ workspaceId: workspace.id });
      if (state.defaultWorkspaceId) setActiveWorkspace(state.defaultWorkspaceId);
      await refreshAppState();
    } catch (err) { setError(err?.message || 'Could not delete workspace.'); }
    finally { setWorkspaceBusy(''); }
  };

  const moveMailbox = async (mailbox, workspaceId) => {
    const mailboxId = mailbox?.mailboxId || mailbox?.id;
    if (!mailboxId || !workspaceId) return;
    setWorkspaceBusy(`move:${mailboxId}`); setError('');
    try { await assignMailboxWorkspace({ mailboxId, workspaceId }); await refreshAppState(); }
    catch (err) { setError(err?.message || 'Could not move mailbox.'); }
    finally { setWorkspaceBusy(''); }
  };

  const disconnectMailbox = async (mailbox) => {
    const mailboxId = mailbox?.mailboxId || mailbox?.id || '';
    const email = mailbox?.email || '';
    const provider = mailbox?.provider || '';
    if (!mailboxId && !email) return;

    const confirmed = window.confirm(
      `Disconnect ${email || 'this mailbox'} from Nudge? Nudge will stop scanning it and hide open opportunities tied to this mailbox. You can reconnect it later.`
    );
    if (!confirmed) return;

    setWorkspaceBusy(`disconnect:${mailboxId || email}`);
    setError('');
    try {
      await disconnectNudgeMailbox({ mailboxId, id: mailboxId, provider, email });
      if (selectedMailboxId === mailboxId) setSelectedMailboxId('');
      await refreshAppState();
    } catch (err) {
      setError(err?.message || 'Could not disconnect mailbox.');
    } finally {
      setWorkspaceBusy('');
    }
  };

  const inviteMember = async () => {
    if (!canManageTeam) return openUpgrade('Team access is available on Agency plans and supported lifetime tiers.');
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    if (inviteRole === 'workspace_manager' && !inviteWorkspaceIds.length) {
      setMemberMessage('Choose at least one workspace for this Workspace Manager.');
      return;
    }
    setMemberBusy('invite'); setMemberMessage(''); setError('');
    try {
      await inviteOrganizationMember({ email, role: inviteRole, workspaceIds: inviteRole === 'admin' ? [] : inviteWorkspaceIds });
      setInviteEmail(''); setInviteWorkspaceIds([]);
      setMemberMessage('Invitation sent. The member will get access after signing in with that email.');
      await refreshAppState();
    } catch (err) { setMemberMessage(err?.message || 'Could not invite this member.'); }
    finally { setMemberBusy(''); }
  };

  const saveMember = async (member) => {
    const memberId = member.id || member.memberId;
    const draft = memberDrafts[memberId] || { role: member.role, workspaceIds: member.workspaceIds || [] };
    if (draft.role === 'workspace_manager' && !draft.workspaceIds?.length) {
      setMemberMessage('Workspace Managers need access to at least one workspace.');
      return;
    }
    setMemberBusy(memberId); setMemberMessage(''); setError('');
    try {
      await updateOrganizationMember({ memberId, role: draft.role, workspaceIds: draft.role === 'admin' ? [] : draft.workspaceIds || [] });
      setMemberMessage(`${member.email} updated.`);
      await refreshAppState();
    } catch (err) { setMemberMessage(err?.message || 'Could not update this member.'); }
    finally { setMemberBusy(''); }
  };

  const removeMember = async (member) => {
    const memberId = member.id || member.memberId;
    if (!window.confirm(`Remove ${member.email} from this NudgeForMe organization?`)) return;
    setMemberBusy(memberId); setMemberMessage('');
    try { await removeOrganizationMember({ memberId }); setMemberMessage(`${member.email} removed.`); await refreshAppState(); }
    catch (err) { setMemberMessage(err?.message || 'Could not remove this member.'); }
    finally { setMemberBusy(''); }
  };

  const resendInvite = async (member) => {
    const memberId = member.id || member.memberId;
    setMemberBusy(memberId); setMemberMessage('');
    try { await resendOrganizationInvite({ memberId }); setMemberMessage(`Invitation resent to ${member.email}.`); }
    catch (err) { setMemberMessage(err?.message || 'Could not resend the invitation.'); }
    finally { setMemberBusy(''); }
  };

  const saveMailboxSettings = async () => {
    if (!selectedMailbox) return;
    const mailboxId = selectedMailbox.mailboxId || selectedMailbox.id;
    setWorkspaceBusy(`settings:${mailboxId}`); setError('');
    try {
      await saveMailboxNudgeSettings({ mailboxId, useWorkspaceDefaults: mailboxUseWorkspaceDefaults, settings: mailboxSettingsDraft });
      await refreshAppState();
      setSaved(true); setTimeout(() => setSaved(false), 2200);
    } catch (err) { setError(err?.message || 'Could not save mailbox settings.'); }
    finally { setWorkspaceBusy(''); }
  };

  const connectProvider = async (provider) => {
    if (!canResearchAndDraft) return openUpgrade('Research & Draft is available on Agency plans and supported lifetime tiers.');
    const apiKey = String(providerKeys[provider] || '').trim();
    if (!apiKey) { setProviderMessage(`Enter your ${providerMeta[provider].label} API key first.`); return; }
    setProviderBusy(`save:${provider}`); setProviderMessage(''); setError('');
    try {
      await saveEnrichmentConnection({ workspaceId: activeWorkspaceId, provider, apiKey });
      setProviderKeys((current) => ({ ...current, [provider]: '' }));
      setProviderMessage(`${providerMeta[provider].label} connected and tested.`);
      await refreshAppState();
    } catch (err) { setProviderMessage(err?.message || `Could not connect ${providerMeta[provider].label}.`); }
    finally { setProviderBusy(''); }
  };

  const testProvider = async (provider) => {
    setProviderBusy(`test:${provider}`); setProviderMessage('');
    try { await testEnrichmentConnection({ workspaceId: activeWorkspaceId, provider }); setProviderMessage(`${providerMeta[provider].label} connection is working.`); await refreshAppState(); }
    catch (err) { setProviderMessage(err?.message || `${providerMeta[provider].label} connection test failed.`); }
    finally { setProviderBusy(''); }
  };

  const disconnectProvider = async (provider) => {
    if (!window.confirm(`Disconnect ${providerMeta[provider].label} from this workspace?`)) return;
    setProviderBusy(`disconnect:${provider}`); setProviderMessage('');
    try { await disconnectEnrichment({ workspaceId: activeWorkspaceId, provider }); setProviderMessage(`${providerMeta[provider].label} disconnected.`); await refreshAppState(); }
    catch (err) { setProviderMessage(err?.message || `Could not disconnect ${providerMeta[provider].label}.`); }
    finally { setProviderBusy(''); }
  };

  const moveResearchProvider = (provider, delta) => {
    setResearchSettingsDraft((current) => {
      const order = [...current.providerOrder];
      const index = order.indexOf(provider);
      const nextIndex = index + delta;
      if (index < 0 || nextIndex < 0 || nextIndex >= order.length) return current;
      [order[index], order[nextIndex]] = [order[nextIndex], order[index]];
      return { ...current, providerOrder: order };
    });
  };

  const persistResearchSettings = async () => {
    setProviderBusy('settings'); setProviderMessage('');
    try {
      await saveResearchSettings({ workspaceId: activeWorkspaceId, settings: researchSettingsDraft });
      setProviderMessage('Research source order saved.');
      await refreshAppState();
    } catch (err) { setProviderMessage(err?.message || 'Could not save research settings.'); }
    finally { setProviderBusy(''); }
  };

  const setCadence = (value, updateFn = updateSettings) => {
    if (value === 'custom') {
      if (!canUseCustomCadence) return openUpgrade('Custom follow-up timing is not included in your current plan.');
      const minDays = Math.max(1, Number(s.minDays || 3));
      const maxDays = Math.max(minDays, Number(s.maxDays || 5));
      updateFn({ cadence: 'custom', minDays, maxDays });
      return;
    }

    const selected = cadenceOptions[value];
    if (!selected) return;
    updateFn({ cadence: value, minDays: selected.minDays, maxDays: selected.maxDays });
  };

  const addTarget = () => {
    const value = targetInput.trim().toLowerCase().replace(/^@/, '');
    if (!value || (s.specificTargets || []).includes(value)) return;
    updateSettings({ specificTargets: [...(s.specificTargets || []), value] });
    setTargetInput('');
  };

  const addBlocked = () => {
    const value = blockedInput.trim().toLowerCase();
    if (!value) return;
    if (value.includes('@') && !value.startsWith('@')) {
      if (!(s.blockedEmails || []).includes(value)) updateSettings({ blockedEmails: [...(s.blockedEmails || []), value] });
    } else {
      const domain = value.replace(/^@/, '');
      if (domain && !(s.blockedDomains || []).includes(domain)) updateSettings({ blockedDomains: [...(s.blockedDomains || []), domain] });
    }
    setBlockedInput('');
  };

  const addAllowed = () => {
    const value = allowedInput.trim().toLowerCase();
    if (!value) return;

    if (value.includes('@') && !value.startsWith('@')) {
      if (!(s.allowedEmails || []).includes(value)) {
        updateSettings({ allowedEmails: [...(s.allowedEmails || []), value] });
      }
    } else {
      const domain = value.replace(/^@/, '');
      if (domain && !(s.allowedDomains || []).includes(domain)) {
        updateSettings({ allowedDomains: [...(s.allowedDomains || []), domain] });
      }
    }

    setAllowedInput('');
  };

  const saveAction = ['automation', 'drafting', 'safety', 'notifications'].includes(section)
    ? <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
    : null;

  const renderOrganization = () => (
    <Stack spacing={2.2}>
      <SectionSurface title="Organization" description="Account-wide controls for all workspaces, mailboxes, and members.">
        <SettingRow title="Organization" note="Owners and admins can manage every workspace from here.">
          <Stack alignItems="flex-end">
            <Typography fontWeight={900}>{state.organization?.name || state.user?.email || 'NudgeForMe organization'}</Typography>
            <Typography variant="caption" color="text.secondary">{teamMembers.length} of {teamMemberLimitLabel} team members · {organizationWorkspaces.length} of {workspaceLimitLabel} workspaces</Typography>
          </Stack>
        </SettingRow>
        <SettingRow last title="Your role" note="Owners control billing. Admins can manage workspaces, mailboxes, and members.">
          <Chip icon={<AdminPanelSettingsRoundedIcon />} label={state.access?.role === 'owner' ? 'Owner' : 'Admin'} />
        </SettingRow>
      </SectionSurface>

      <SectionSurface title="Workspaces" description="Create, rename, and organize client/business spaces at the organization level.">
        {organizationWorkspaces.map((workspace, index) => {
          const mailboxCount = organizationMailboxes.filter((mailbox) => mailbox.workspaceId === workspace.id).length;
          const memberCount = teamMembers.filter((member) => member.role === 'owner' || member.role === 'admin' || (member.workspaceIds || []).includes(workspace.id)).length;
          return (
            <SettingRow key={workspace.id} last={false} title={workspace.name} note={`${mailboxCount} mailbox${mailboxCount === 1 ? '' : 'es'} · ${memberCount} member${memberCount === 1 ? '' : 's'}${workspace.isDefault ? ' · Default' : ''}`}>
              <Stack direction="row" spacing={.7} justifyContent="flex-end">
                <Button size="small" variant="text" onClick={() => { setActiveWorkspace(workspace.id); goSection('workspace'); }}>Open</Button>
                <Button size="small" variant="outlined" onClick={() => renameWorkspace(workspace)} disabled={Boolean(workspaceBusy)}>Rename</Button>
                {!workspace.isDefault && <Button size="small" color="error" variant="text" onClick={() => removeWorkspace(workspace)} disabled={Boolean(workspaceBusy)}>Delete</Button>}
              </Stack>
            </SettingRow>
          );
        })}
        <Box sx={{ py: 2.2 }}>
          <Typography fontWeight={850}>Create workspace</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: .4 }}>New workspaces copy the active workspace's follow-up settings so you can customize from a sensible default.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 1.5, maxWidth: 620 }}>
            <TextField size="small" fullWidth placeholder="Client or business name" value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} />
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={createWorkspace} disabled={!workspaceName.trim() || Boolean(workspaceBusy) || (!unlimitedWorkspaces && organizationWorkspaces.length >= workspaceLimit)}>Create</Button>
          </Stack>
        </Box>
      </SectionSurface>
    </Stack>
  );

  const renderAllMailboxes = () => (
    <SectionSurface title="All mailboxes" description="Organization-wide mailbox administration. Move a mailbox to another workspace or disconnect it from Nudge." action={<Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => navigate('/setup')}>Connect mailbox</Button>}>
      {organizationMailboxes.length ? organizationMailboxes.map((mailbox, index) => {
        const mailboxId = mailbox.mailboxId || mailbox.id;
        const workspace = organizationWorkspaces.find((item) => item.id === mailbox.workspaceId);
        const disconnectBusy = workspaceBusy === `disconnect:${mailboxId || mailbox.email}`;
        return (
          <SettingRow key={mailboxId} last={index === organizationMailboxes.length - 1} title={mailbox.email || 'Mailbox'} note={`${providerLabel(mailbox.provider)} · ${workspace?.name || 'Unassigned'} · ${mailbox.health || 'connected'}`}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Select
                size="small"
                value={mailbox.workspaceId || state.defaultWorkspaceId || ''}
                onChange={(event) => moveMailbox(mailbox, event.target.value)}
                disabled={workspaceBusy === `move:${mailboxId}` || disconnectBusy}
                sx={{ minWidth: 180 }}
              >
                {organizationWorkspaces.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
              </Select>
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => disconnectMailbox(mailbox)}
                disabled={Boolean(workspaceBusy)}
                sx={{ whiteSpace: 'nowrap', px: .8 }}
              >
                {disconnectBusy ? 'Disconnecting…' : 'Disconnect'}
              </Button>
            </Stack>
          </SettingRow>
        );
      }) : <Alert severity="info" sx={{ my: 2 }}>No mailbox is connected yet.</Alert>}
    </SectionSurface>
  );

  const renderMembers = () => (
    <Stack spacing={2.2}>
      <SectionSurface title="Team members" description={`Invite people to manage assigned workspaces. Your plan includes ${teamMemberLimitLabel} team member${unlimitedTeamMembers || teamMemberLimit !== 1 ? 's' : ''}, including the owner.`}>
        {!canManageTeam && <Alert severity="info" sx={{ my: 2 }}>Team access is available on Agency plans and supported lifetime tiers. Upgrade to invite Workspace Managers or Admins.</Alert>}
        <Box sx={{ py: 2.2 }}>
          <Typography fontWeight={850}>Invite member</Typography>
          <Grid container spacing={1.2} sx={{ mt: .4 }} alignItems="center">
            <Grid item xs={12} md={4}><TextField size="small" fullWidth label="Email" value={inviteEmail} disabled={!canManageTeam} onChange={(event) => setInviteEmail(event.target.value)} /></Grid>
            <Grid item xs={12} md={3}>
              <Select size="small" fullWidth value={inviteRole} disabled={!canManageTeam} onChange={(event) => { setInviteRole(event.target.value); if (event.target.value === 'admin') setInviteWorkspaceIds([]); }}>
                <MenuItem value="workspace_manager">Workspace Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={3}>
              <Select
                size="small"
                multiple
                fullWidth
                displayEmpty
                disabled={!canManageTeam || inviteRole === 'admin'}
                value={inviteWorkspaceIds}
                onChange={(event) => setInviteWorkspaceIds(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
                renderValue={(selected) => inviteRole === 'admin' ? 'All workspaces' : selected.length ? `${selected.length} workspace${selected.length === 1 ? '' : 's'}` : 'Choose workspaces'}
              >
                {organizationWorkspaces.map((workspace) => <MenuItem key={workspace.id} value={workspace.id}><Checkbox checked={inviteWorkspaceIds.includes(workspace.id)} size="small" /><Typography variant="body2">{workspace.name}</Typography></MenuItem>)}
              </Select>
            </Grid>
            <Grid item xs={12} md={2}><Button fullWidth variant="contained" startIcon={<PersonAddAltRoundedIcon />} onClick={inviteMember} disabled={!canManageTeam || !inviteEmail.trim() || memberBusy === 'invite' || (!unlimitedTeamMembers && teamMembers.filter((member) => ['active', 'invited'].includes(member.status)).length >= teamMemberLimit)}>{memberBusy === 'invite' ? 'Inviting…' : 'Invite'}</Button></Grid>
          </Grid>
        </Box>
      </SectionSurface>

      <SectionSurface title="People" description="Admins can work across all workspaces. Workspace Managers only see the workspaces assigned to them.">
        {teamMembers.map((member, index) => {
          const memberId = member.id || member.memberId;
          const draft = memberDrafts[memberId] || { role: member.role, workspaceIds: member.workspaceIds || [] };
          const isOwner = member.role === 'owner';
          return (
            <Box key={memberId} sx={{ py: 2.2, borderBottom: index === teamMembers.length - 1 ? 0 : '1px solid rgba(26,29,49,.08)' }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={3.3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: 'rgba(26,29,49,.08)', display: 'grid', placeItems: 'center', fontWeight: 900 }}>{String(member.email || '?').slice(0, 1).toUpperCase()}</Box>
                    <Box minWidth={0}><Typography fontWeight={850} noWrap>{member.email}</Typography><Typography variant="caption" color="text.secondary">{member.status === 'invited' ? 'Invitation pending' : 'Active'}</Typography></Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={5} md={2.2}>
                  <Select size="small" fullWidth disabled={isOwner || !canManageTeam} value={isOwner ? 'owner' : draft.role} onChange={(event) => setMemberDrafts((current) => ({ ...current, [memberId]: { ...draft, role: event.target.value, workspaceIds: event.target.value === 'admin' ? [] : draft.workspaceIds } }))}>
                    {isOwner && <MenuItem value="owner">Owner</MenuItem>}
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="workspace_manager">Workspace Manager</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={7} md={3.5}>
                  {isOwner || draft.role === 'admin' ? <Typography variant="body2" color="text.secondary">All workspaces</Typography> : (
                    <Select size="small" multiple fullWidth disabled={!canManageTeam} value={draft.workspaceIds || []} onChange={(event) => setMemberDrafts((current) => ({ ...current, [memberId]: { ...draft, workspaceIds: typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value } }))} renderValue={(selected) => selected.length ? `${selected.length} workspace${selected.length === 1 ? '' : 's'}` : 'No workspace'}>
                      {organizationWorkspaces.map((workspace) => <MenuItem key={workspace.id} value={workspace.id}><Checkbox checked={(draft.workspaceIds || []).includes(workspace.id)} size="small" /><Typography variant="body2">{workspace.name}</Typography></MenuItem>)}
                    </Select>
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={.6} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
                    {!isOwner && <Button size="small" variant="outlined" onClick={() => saveMember(member)} disabled={!canManageTeam || memberBusy === memberId}>Save</Button>}
                    {!isOwner && member.status === 'invited' && <Button size="small" variant="text" onClick={() => resendInvite(member)} disabled={!canManageTeam || memberBusy === memberId}>Resend</Button>}
                    {!isOwner && <Button size="small" color="error" variant="text" onClick={() => removeMember(member)} disabled={!canManageTeam || memberBusy === memberId}>Remove</Button>}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </SectionSurface>
      {memberMessage && <Alert severity={/sent|updated|removed|resent/i.test(memberMessage) ? 'success' : 'info'}>{memberMessage}</Alert>}
    </Stack>
  );

  const renderWorkspace = () => (
    <Stack spacing={2.2}>
      <SectionSurface title={activeWorkspace?.name || 'Workspace'} description="This page is scoped to the active workspace. Mailboxes, follow-up rules, enrichment, and opportunities from other workspaces stay out of view.">
        <SettingRow title="Active workspace" note="Switch context here or from the app header.">
          <Select size="small" fullWidth value={activeWorkspaceId} onChange={(event) => setActiveWorkspace(event.target.value)}>
            {(state.workspaces || []).map((workspace) => <MenuItem key={workspace.id} value={workspace.id}>{workspace.name}{workspace.isDefault ? ' · Default' : ''}</MenuItem>)}
          </Select>
        </SettingRow>
        <SettingRow title="Mailboxes in this workspace" note="Only mailboxes assigned to this workspace appear in its settings and opportunity views.">
          <Typography fontWeight={900} textAlign="right">{workspaceMailboxes.length}</Typography>
        </SettingRow>
        <SettingRow last title="Access" note="Workspace Managers only see the workspace(s) assigned to them. Owners and Admins can switch across the organization.">
          <Chip label={canManageOrganization ? 'Organization-wide access' : 'Workspace access'} />
        </SettingRow>
      </SectionSurface>
      {canManageOrganization && <Alert severity="info">Need to rename this workspace, create another one, move mailboxes, or manage members? Use <Button size="small" onClick={() => goSection('organization')}>Organization settings</Button>.</Alert>}
    </Stack>
  );

  const renderMailboxes = () => (
    <Stack spacing={2.2}>
      <SectionSurface title="Workspace mailboxes" description={`Only mailboxes assigned to ${activeWorkspace?.name || 'this workspace'} are shown here.`} action={canManageOrganization ? <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => navigate('/setup')}>Connect mailbox</Button> : null}>
        {workspaceMailboxes.length ? workspaceMailboxes.map((mailbox, index) => {
          const mailboxId = mailbox.mailboxId || mailbox.id;
          const disconnectBusy = workspaceBusy === `disconnect:${mailboxId || mailbox.email}`;
          return (
            <SettingRow key={mailboxId} last={index === workspaceMailboxes.length - 1} title={mailbox.email || 'Mailbox'} note={`${providerLabel(mailbox.provider)} · ${mailbox.health || 'connected'} · ${mailbox.settingsMode === 'custom' ? 'Custom rules' : 'Workspace defaults'}`}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Chip size="small" label={mailbox.settingsMode === 'custom' ? 'Custom rules' : 'Inherited'} />
                {canManageOrganization && (
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    onClick={() => disconnectMailbox(mailbox)}
                    disabled={Boolean(workspaceBusy)}
                    sx={{ whiteSpace: 'nowrap', px: .8 }}
                  >
                    {disconnectBusy ? 'Disconnecting…' : 'Disconnect'}
                  </Button>
                )}
              </Stack>
            </SettingRow>
          );
        }) : <Alert severity="info" sx={{ my: 2 }}>No mailbox is assigned to this workspace.</Alert>}
      </SectionSurface>

      <SectionSurface title="Mailbox-specific rules" description="Most mailboxes should inherit workspace rules. Add an override only when this inbox needs different behavior." action={<Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={saveMailboxSettings} disabled={!selectedMailbox || Boolean(workspaceBusy)}>Save mailbox rules</Button>}>
        <SettingRow title="Mailbox" note="Only mailboxes in the active workspace are available.">
          <Select size="small" fullWidth value={selectedMailboxId} onChange={(event) => setSelectedMailboxId(event.target.value)}>
            {workspaceMailboxes.map((mailbox) => <MenuItem key={mailbox.mailboxId || mailbox.id} value={mailbox.mailboxId || mailbox.id}>{mailbox.email}</MenuItem>)}
          </Select>
        </SettingRow>
        {selectedMailbox && (
          <>
            <SettingRow title="Use workspace defaults" note="Recommended. Changes to this workspace automatically apply to the mailbox.">
              <Switch checked={mailboxUseWorkspaceDefaults} onChange={(event) => setMailboxUseWorkspaceDefaults(event.target.checked)} />
            </SettingRow>
            {!mailboxUseWorkspaceDefaults && (
              <Box sx={{ py: 1 }}>
                <SettingRow title="Follow-up mode" note="Override whether this mailbox drafts or sends automatically.">
                  <Select size="small" fullWidth value={mailboxSettingsDraft.mode || 'draft'} onChange={(event) => setMailboxSettingsDraft((current) => ({ ...current, mode: event.target.value }))}>
                    <MenuItem value="draft">Draft in mailbox</MenuItem>
                    <MenuItem value="send" disabled={!canAutoSend}>Send automatically</MenuItem>
                  </Select>
                </SettingRow>
                <SettingRow title="Discovery lookback" note="Override how far back this mailbox scans.">
                  <Select size="small" fullWidth value={Number(mailboxSettingsDraft.lookbackDays || s.lookbackDays || 30)} onChange={(event) => setMailboxSettingsDraft((current) => ({ ...current, lookbackDays: Number(event.target.value) }))}>
                    {lookbackOptions.filter((days) => days <= maxLookbackDays).map((days) => <MenuItem key={days} value={days}>{lookbackLabel(days)}</MenuItem>)}
                  </Select>
                </SettingRow>
                <SettingRow title="Follow-up pace" note="Override the workspace timing for this inbox.">
                  <Select size="small" fullWidth value={mailboxSettingsDraft.cadence || 'balanced'} onChange={(event) => {
                    const value = event.target.value;
                    if (value === 'custom') {
                      if (!canUseCustomCadence) return openUpgrade('Custom follow-up timing is not included in your current plan.');
                      setMailboxSettingsDraft((current) => {
                        const minDays = Math.max(1, Number(current.minDays || s.minDays || 3));
                        const maxDays = Math.max(minDays, Number(current.maxDays || s.maxDays || 5));
                        return { ...current, cadence: 'custom', minDays, maxDays };
                      });
                      return;
                    }
                    const selected = cadenceOptions[value];
                    if (!selected) return;
                    setMailboxSettingsDraft((current) => ({ ...current, cadence: value, minDays: selected.minDays, maxDays: selected.maxDays }));
                  }}>
                    {Object.entries(cadenceOptions).map(([id, item]) => <MenuItem key={id} value={id}>{item.label} · {item.note}</MenuItem>)}
                    {canUseCustomCadence && <MenuItem value="custom">Custom</MenuItem>}
                  </Select>
                </SettingRow>
                {canUseCustomCadence && mailboxSettingsDraft.cadence === 'custom' && (
                  <SettingRow title="Custom wait" note="Choose the minimum and maximum number of days for this mailbox.">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField size="small" type="number" label="Min" inputProps={{ min: 1, max: 90 }} value={mailboxSettingsDraft.minDays || 3} onChange={(event) => {
                        const minDays = Math.max(1, Math.min(90, Number(event.target.value || 1)));
                        setMailboxSettingsDraft((current) => ({ ...current, cadence: 'custom', minDays, maxDays: Math.max(minDays, Number(current.maxDays || 5)) }));
                      }} sx={{ width: 90 }} />
                      <TextField size="small" type="number" label="Max" inputProps={{ min: 1, max: 90 }} value={mailboxSettingsDraft.maxDays || 5} onChange={(event) => {
                        const maxDays = Math.max(1, Math.min(90, Number(event.target.value || 1)));
                        setMailboxSettingsDraft((current) => ({ ...current, cadence: 'custom', minDays: Math.min(Number(current.minDays || 3), maxDays), maxDays }));
                      }} sx={{ width: 90 }} />
                    </Stack>
                  </SettingRow>
                )}
                <SettingRow title="Writing instructions" note="Optional mailbox-only guidance.">
                  <TextField size="small" multiline minRows={3} fullWidth disabled={!canUseCustomInstructions} value={mailboxSettingsDraft.customInstructions || ''} onChange={(event) => setMailboxSettingsDraft((current) => ({ ...current, customInstructions: event.target.value.slice(0, 1200) }))} />
                </SettingRow>
              </Box>
            )}
          </>
        )}
      </SectionSurface>
    </Stack>
  );

  const renderAutomation = () => (
    <SectionSurface title="Automation" description="Control discovery, sequence length, timing, and whether Nudge drafts or sends." action={saveAction}>
      <SettingRow title="Agent status" note={canManageOrganization ? 'This master switch pauses or resumes Nudge across connected workspaces.' : 'Only the organization Owner or Admin can pause or resume the whole Nudge agent.'}>
        <FormControlLabel sx={{ m: 0, justifyContent: 'flex-end' }} control={<Switch checked={state.active} disabled={!canManageOrganization} onChange={async (event) => {
          const active = event.target.checked; setError('');
          try { await setNudgeStatus(active); update({ active }); }
          catch (err) { setError(err?.message || 'Could not change agent status.'); }
        }} />} label={state.active ? 'Active' : 'Paused'} />
      </SettingRow>
      <SettingRow title="Discovery" note="Automatically find conversations where you sent the last email and a follow-up may still be useful.">
        <Switch checked={s.automaticDiscovery !== false} onChange={(event) => updateSettings({ automaticDiscovery: event.target.checked })} />
      </SettingRow>
      <SettingRow title="Discovery lookback" note={`Your plan allows up to ${lookbackLabel(maxLookbackDays)}.`}>
        <Select size="small" fullWidth value={Number(s.lookbackDays || 30)} onChange={(event) => updateSettings({ lookbackDays: Number(event.target.value) })}>
          {lookbackOptions.filter((days) => days <= maxLookbackDays).map((days) => <MenuItem key={days} value={days}>{lookbackLabel(days)}</MenuItem>)}
        </Select>
      </SettingRow>
      <SettingRow title="Follow-up mode" note="Draft mode keeps you in control. Automatic sending is available only when your plan allows it.">
        <ToggleButtonGroup exclusive size="small" value={s.mode || 'draft'} onChange={(_, value) => {
          if (!value) return;
          if (value === 'send' && !canAutoSend) return openUpgrade('Automatic sending is not included in your current plan.');
          updateSettings({ mode: value });
        }}>
          <ToggleButton value="draft">Draft</ToggleButton>
          <ToggleButton value="send">Auto-send {!canAutoSend && <LockRoundedIcon sx={{ ml: .5, fontSize: 15 }} />}</ToggleButton>
        </ToggleButtonGroup>
      </SettingRow>
      <SettingRow title="Follow-ups per opportunity" note={canContinueUntilReply ? `Continue until a reply, with Nudge safety limits. Current internal cap: ${maxFollowUps}.` : `Up to ${maxFollowUps} follow-ups on your current plan.`}>
        <Select size="small" fullWidth value={s.continueUntilReply ? 'until-reply' : Math.min(maxFollowUps, Number(s.attempts || 3))} onChange={(event) => {
          const value = event.target.value;
          if (value === 'until-reply') updateSettings({ continueUntilReply: true, attempts: maxFollowUps });
          else updateSettings({ continueUntilReply: false, attempts: Number(value) });
        }}>
          {[3, 5, 7, 10, 12].filter((value) => value <= maxFollowUps).map((value) => <MenuItem key={value} value={value}>Up to {value}</MenuItem>)}
          {canContinueUntilReply && <MenuItem value="until-reply">Until reply</MenuItem>}
        </Select>
      </SettingRow>
      <SettingRow title="Follow-up timing" note={canUseCustomCadence ? 'Choose a preset or customize the wait between attempts.' : 'Choose a simple preset pace.'}>
        <Select size="small" fullWidth value={s.cadence || 'balanced'} onChange={(event) => setCadence(event.target.value)}>
          {Object.entries(cadenceOptions).map(([id, item]) => <MenuItem key={id} value={id}>{item.label} · {item.note}</MenuItem>)}
          {canUseCustomCadence && <MenuItem value="custom">Custom</MenuItem>}
        </Select>
      </SettingRow>
      {canUseCustomCadence && s.cadence === 'custom' && (
        <SettingRow title="Custom wait" note="Choose a minimum and maximum number of days.">
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField size="small" type="number" label="Min" inputProps={{ min: 1, max: 90 }} value={s.minDays || 3} onChange={(event) => {
              const minDays = Math.max(1, Math.min(90, Number(event.target.value || 1)));
              updateSettings({ minDays, maxDays: Math.max(minDays, Number(s.maxDays || 5)) });
            }} sx={{ width: 90 }} />
            <TextField size="small" type="number" label="Max" inputProps={{ min: 1, max: 90 }} value={s.maxDays || 5} onChange={(event) => {
              const maxDays = Math.max(1, Math.min(90, Number(event.target.value || 1)));
              updateSettings({ minDays: Math.min(Number(s.minDays || 3), maxDays), maxDays });
            }} sx={{ width: 90 }} />
          </Stack>
        </SettingRow>
      )}
      <SettingRow title="Create due drafts automatically" note="When enabled, Nudge creates drafts after a due opportunity is confirmed, instead of waiting for a manual Draft click.">
        <Switch checked={Boolean(s.autoCreateDrafts) && canAutoCreateDrafts} onChange={(event) => {
          if (event.target.checked && !canAutoCreateDrafts) return openUpgrade('Automatic draft creation is not included in your current plan.');
          updateSettings({ autoCreateDrafts: event.target.checked });
        }} />
      </SettingRow>
      <Box sx={{ py: 2.2 }}>
        <Typography fontWeight={850}>Timing preferences</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" useFlexGap gap={1.2} sx={{ mt: 1.2 }}>
          <FormControlLabel control={<Switch checked={s.smartTiming !== false} onChange={(event) => updateSettings({ smartTiming: event.target.checked })} />} label="Smart timing" />
          <FormControlLabel control={<Switch checked={s.varyNaturally !== false} onChange={(event) => updateSettings({ varyNaturally: event.target.checked })} />} label="Vary naturally" />
          <FormControlLabel control={<Switch checked={s.recipientLocalTime !== false} onChange={(event) => updateSettings({ recipientLocalTime: event.target.checked })} />} label="Recipient local time" />
          <FormControlLabel control={<Switch checked={s.avoidWeekends !== false} onChange={(event) => updateSettings({ avoidWeekends: event.target.checked })} />} label="Avoid weekends" />
        </Stack>
      </Box>
      <Box sx={{ py: 2.2, borderTop: '1px solid rgba(26,29,49,.08)' }}>
        <Typography fontWeight={850}>Discovery focus</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: .4 }}>Optional. Add a person or domain you specifically want Nudge to monitor.</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.4, maxWidth: 650 }}>
          <TextField size="small" fullWidth value={targetInput} onChange={(event) => setTargetInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addTarget(); } }} placeholder="alex@example.com or example.com" />
          <Button variant="outlined" onClick={addTarget}>Add</Button>
        </Stack>
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={.8} sx={{ mt: 1.3 }}>
          {(s.specificTargets || []).map((value) => <Chip key={value} label={value.includes('@') ? value : `@${value}`} onDelete={() => updateSettings({ specificTargets: (s.specificTargets || []).filter((item) => item !== value) })} />)}
        </Stack>
      </Box>
    </SectionSurface>
  );

  const renderDrafting = () => (
    <SectionSurface title="Drafting" description="Control what Nudge writes and what it carries forward from the conversation." action={saveAction}>
      <Box sx={{ py: 2.2 }}>
        <Typography fontWeight={850}>Writing instructions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: .45 }}>Applied to every generated follow-up in this workspace unless they conflict with safety rules.</Typography>
        <TextField fullWidth multiline minRows={5} disabled={!canUseCustomInstructions} value={canUseCustomInstructions ? s.customInstructions || '' : ''} onChange={(event) => updateSettings({ customInstructions: event.target.value.slice(0, 1200) })} placeholder={canUseCustomInstructions ? 'Example: Be warm but concise. Refer to the open question. Avoid generic bump language.' : 'Custom writing instructions are not included in this plan.'} sx={{ mt: 1.5 }} inputProps={{ maxLength: 1200 }} />
      </Box>
      <SettingRow title="Include previous thread" note="Adds short prior context beneath the new follow-up when creating the draft.">
        <Switch checked={s.includeThreadHistoryInDrafts !== false} onChange={(event) => updateSettings({ includeThreadHistoryInDrafts: event.target.checked })} />
      </SettingRow>
      <SettingRow title="Reuse previous attachments" note="Copies small attachments from the previous sent message when the provider supports it.">
        <Switch checked={Boolean(s.includeThreadAttachments) && canUseAttachments} onChange={(event) => {
          if (event.target.checked && !canUseAttachments) return openUpgrade('Reusing previous attachments is not included in your current plan.');
          updateSettings({ includeThreadAttachments: event.target.checked });
        }} />
      </SettingRow>
      <SettingRow last title="Nudge branding" note={canRemoveBranding ? 'Choose whether generated drafts include the small “Drafted by NudgeForMe” line.' : 'Branding removal is not included in this plan.'}>
        <FormControlLabel sx={{ m: 0 }} control={<Switch checked={Boolean(s.removeNudgeBranding) && canRemoveBranding} onChange={(event) => {
          if (event.target.checked && !canRemoveBranding) return openUpgrade('Branding removal is not included in your current plan.');
          updateSettings({ removeNudgeBranding: event.target.checked });
        }} />} label="Remove branding" />
      </SettingRow>
    </SectionSurface>
  );

  const renderSafety = () => {
    const autoSendPolicy = ['approval', 'allowlist', 'all'].includes(String(s.autoSendPolicy || '').toLowerCase())
      ? String(s.autoSendPolicy).toLowerCase()
      : 'approval';

    return (
      <SectionSurface
        title="Safety"
        description="Choose how automatic sending is authorized. Safety checks and your Never follow up list always win."
        action={saveAction}
      >
        {canAutoSend && (
          <>
            <Box sx={{ py: 2.2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} gap={2}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack direction="row" spacing={1.1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography fontWeight={900}>Who can Nudge auto-send to?</Typography>
                    {(s.mode || 'draft') !== 'send' && (
                      <Chip size="small" label="Auto-send is currently off" variant="outlined" />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .5, lineHeight: 1.6 }}>
                    This only controls automatic sending. You can still create drafts manually for any eligible conversation.
                  </Typography>
                </Box>
              </Stack>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={autoSendPolicy}
                onChange={(_, value) => value && updateSettings({ autoSendPolicy: value })}
                sx={{
                  mt: 1.8,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  '& .MuiToggleButtonGroup-grouped': {
                    border: '1px solid rgba(26,29,49,.13)!important',
                    borderRadius: '999px!important',
                    px: 1.5,
                  },
                }}
              >
                <ToggleButton value="approval">Approve each conversation</ToggleButton>
                <ToggleButton value="allowlist">Approved recipients only</ToggleButton>
                <ToggleButton value="all">Anyone except blocked</ToggleButton>
              </ToggleButtonGroup>

              {autoSendPolicy === 'approval' && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" fontWeight={800}>Best for maximum control</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .35, lineHeight: 1.55 }}>
                    You do not need to add senders to a list. Nudge will wait for you to approve each individual conversation before it can send automatically.
                    If you do not approve it, it will not send.
                  </Typography>
                </Box>
              )}

              {autoSendPolicy === 'allowlist' && (
                <Box sx={{ mt: 1.7 }}>
                  <Typography variant="body2" fontWeight={800}>Only these recipients can receive automatic follow-ups</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .35, lineHeight: 1.55 }}>
                    Add an email address once, or add a whole domain. Eligible conversations with those recipients can auto-send without per-conversation approval.
                    Anyone not on this list will not be auto-sent to.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.3, maxWidth: 720 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={allowedInput}
                      onChange={(event) => setAllowedInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addAllowed();
                        }
                      }}
                      placeholder="person@example.com or @example.com"
                    />
                    <Button variant="outlined" onClick={addAllowed}>Allow</Button>
                  </Stack>

                  <Stack direction="row" flexWrap="wrap" useFlexGap gap={.8} sx={{ mt: 1.2 }}>
                    {(s.allowedEmails || []).map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        onDelete={() => updateSettings({ allowedEmails: (s.allowedEmails || []).filter((item) => item !== value) })}
                      />
                    ))}
                    {(s.allowedDomains || []).map((value) => (
                      <Chip
                        key={value}
                        label={`@${value}`}
                        onDelete={() => updateSettings({ allowedDomains: (s.allowedDomains || []).filter((item) => item !== value) })}
                      />
                    ))}
                  </Stack>

                  {!(s.allowedEmails || []).length && !(s.allowedDomains || []).length && (
                    <Alert severity="info" sx={{ mt: 1.3 }}>
                      Your approved-recipient list is empty, so Nudge will not auto-send to anyone.
                    </Alert>
                  )}
                </Box>
              )}

              {autoSendPolicy === 'all' && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" fontWeight={800}>No manual approval list required</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .35, lineHeight: 1.55 }}>
                    Nudge may auto-send to any eligible recipient unless that email address or domain is in Never follow up.
                    Reply, bounce, out-of-office, no-reply, and other safety checks still apply before sending.
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />
          </>
        )}

        {!canAutoSend && (
          <>
            <Box sx={{ py: 2.2 }}>
              <Typography fontWeight={900}>Automatic sending</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: .4, lineHeight: 1.55 }}>
                Your current plan does not include auto-send. The protections below still apply to discovery and drafting.
              </Typography>
            </Box>
            <Divider />
          </>
        )}

        <Box sx={{ py: 2.2 }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <SecurityRoundedIcon color="success" />
            <Typography fontWeight={900}>Always-on protections</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: .45, lineHeight: 1.55 }}>
            These checks apply regardless of which auto-send option you choose.
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ mt: 1.4 }}>
            {['Stop when a reply arrives', 'Stop on bounce', 'Pause on out-of-office', 'Block automated/no-reply addresses'].map((label) => (
              <Chip key={label} icon={<CheckCircleOutlineRoundedIcon />} label={label} variant="outlined" />
            ))}
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ py: 2.2 }}>
          <Typography fontWeight={900}>Never follow up</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: .4, lineHeight: 1.55 }}>
            This is a hard block. Nudge will not draft or auto-send follow-ups to these email addresses or domains, even if they are approved above.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.4, maxWidth: 720 }}>
            <TextField
              size="small"
              fullWidth
              value={blockedInput}
              onChange={(event) => setBlockedInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addBlocked();
                }
              }}
              placeholder="person@example.com or @example.com"
            />
            <Button variant="outlined" onClick={addBlocked}>Block</Button>
          </Stack>
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={.8} sx={{ mt: 1.3 }}>
            {(s.blockedEmails || []).map((value) => (
              <Chip
                key={value}
                label={value}
                onDelete={() => updateSettings({ blockedEmails: (s.blockedEmails || []).filter((item) => item !== value) })}
              />
            ))}
            {(s.blockedDomains || []).map((value) => (
              <Chip
                key={value}
                label={`@${value}`}
                onDelete={() => updateSettings({ blockedDomains: (s.blockedDomains || []).filter((item) => item !== value) })}
              />
            ))}
          </Stack>
        </Box>
      </SectionSurface>
    );
  };


  const renderEnrichment = () => {
    const order = researchSettingsDraft.providerOrder || ['cleanlist', 'apollo', 'tavily'];
    return (
      <Stack spacing={2.2}>
        {!canResearchAndDraft && <Alert severity="info" action={<Button color="inherit" onClick={() => openUpgrade('Research & Draft is available on Agency plans and supported lifetime tiers.')}>View upgrade</Button>}>Research & Draft is not included in your current plan.</Alert>}
        <SectionSurface title="Enrichment" description="Research & Draft can combine structured B2B enrichment with recent public web signals. Sources are workspace-specific." action={<Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={persistResearchSettings} disabled={!canResearchAndDraft || providerBusy === 'settings'}>{providerBusy === 'settings' ? 'Saving…' : 'Save research settings'}</Button>}>
          <SettingRow title="Research mode" note="Waterfall checks sources in order and stops once Nudge finds a genuinely usable personalization angle. Use all sources when quality matters more than enrichment credits.">
            <Select size="small" fullWidth value={researchSettingsDraft.strategy} onChange={(event) => setResearchSettingsDraft((current) => ({ ...current, strategy: event.target.value }))} disabled={!canResearchAndDraft}>
              <MenuItem value="waterfall">Waterfall · recommended</MenuItem>
              <MenuItem value="all">Use all connected sources</MenuItem>
            </Select>
          </SettingRow>
          <SettingRow last title="Stop when enough context is found" note="In waterfall mode, Nudge keeps researching past generic firmographics until it can identify a useful angle for this specific conversation.">
            <Switch checked={researchSettingsDraft.stopWhenEnough !== false} disabled={!canResearchAndDraft || researchSettingsDraft.strategy === 'all'} onChange={(event) => setResearchSettingsDraft((current) => ({ ...current, stopWhenEnough: event.target.checked }))} />
          </SettingRow>
        </SectionSurface>

        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(26,29,49,.09)', borderRadius: 2.5, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2.2, sm: 3 }, py: 2.4, bgcolor: 'rgba(26,29,49,.018)', borderBottom: '1px solid rgba(26,29,49,.08)' }}>
            <Typography variant="h3" sx={{ fontSize: 23 }}>Research sources</Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: .45 }}>Drag-free ordering for now: use the arrows to choose which source Nudge checks first.</Typography>
          </Box>
          {order.map((provider, index) => {
            const meta = providerMeta[provider];
            const status = activeWorkspace?.integrations?.[provider] || { connected: false };
            const allowed = canResearchAndDraft && allowedEnrichmentProviders.includes(provider);
            return (
              <Box key={provider} sx={{ px: { xs: 2.2, sm: 3 }, py: 2.4, borderBottom: index === order.length - 1 ? 0 : '1px solid rgba(26,29,49,.08)' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={5}>
                    <Stack direction="row" spacing={1.4} alignItems="flex-start">
                      <Box sx={{ width: 34, height: 34, borderRadius: 1.3, display: 'grid', placeItems: 'center', bgcolor: provider === 'tavily' ? 'rgba(141,149,255,.13)' : 'rgba(255,182,72,.14)', color: '#101228', fontWeight: 900 }}>{index + 1}</Box>
                      <Box>
                        <Stack direction="row" spacing={.8} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography fontWeight={900}>{meta.label}</Typography>
                          <Chip size="small" label={meta.short} variant="outlined" />
                          {status.connected && <Chip size="small" color="success" label="Connected" />}
                          {!allowed && <Chip size="small" icon={<LockRoundedIcon />} label="Locked" />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: .65, lineHeight: 1.55 }}>{meta.description}</Typography>
                        {status.keyHint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: .7 }}>Key: {status.keyHint}</Typography>}
                        {status.lastError && <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: .5 }}>{status.lastError}</Typography>}
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Stack spacing={1.1}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField size="small" fullWidth type="password" placeholder={status.connected ? `Paste a new ${meta.label} key to replace the current connection` : meta.placeholder} value={providerKeys[provider] || ''} disabled={!allowed} onChange={(event) => setProviderKeys((current) => ({ ...current, [provider]: event.target.value }))} />
                        <Button variant={status.connected ? 'outlined' : 'contained'} onClick={() => connectProvider(provider)} disabled={!allowed || providerBusy === `save:${provider}`}>{providerBusy === `save:${provider}` ? 'Testing…' : status.connected ? 'Replace key' : 'Connect'}</Button>
                      </Stack>
                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                        <Stack direction="row" spacing={.5}>
                          <Button size="small" color="inherit" startIcon={<KeyboardArrowUpRoundedIcon />} disabled={index === 0 || !allowed} onClick={() => moveResearchProvider(provider, -1)}>Move Up</Button>
                          <Button size="small" color="inherit" startIcon={<KeyboardArrowDownRoundedIcon />} disabled={index === order.length - 1 || !allowed} onClick={() => moveResearchProvider(provider, 1)}>Move Down</Button>
                        </Stack>
                        {status.connected && (
                          <Stack direction="row" spacing={.6}>
                            <Button size="small" variant="text" onClick={() => testProvider(provider)} disabled={Boolean(providerBusy)}>Test</Button>
                            <Button size="small" color="error" variant="text" onClick={() => disconnectProvider(provider)} disabled={Boolean(providerBusy)}>Disconnect</Button>
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </Box>
        {providerMessage && <Alert severity={/working|connected|saved/i.test(providerMessage) ? 'success' : 'info'}>{providerMessage}</Alert>}
        <Alert severity="info">Structured enrichment can identify who the recipient/company is, but that alone may not improve the wording. Web research is designed to add recent public signals; Nudge now continues through the research order until it finds a usable angle instead of stopping just because it found a title and company.</Alert>
      </Stack>
    );
  };

  const renderNotifications = () => (
    <SectionSurface title="Notifications" description="Choose which NudgeForMe emails you want to receive." action={saveAction}>
      {notificationOptions.map((item, index) => (
        <SettingRow key={item.key} last={index === notificationOptions.length - 1} title={item.label} note={item.note}>
          <Switch checked={s.emailPrefs?.[item.key] !== false} onChange={(event) => updateSettings({ emailPrefs: { ...(s.emailPrefs || {}), [item.key]: event.target.checked } })} />
        </SettingRow>
      ))}
    </SectionSurface>
  );

  const renderCredits = () => (
    <Stack spacing={2.2}>
      <SectionSurface title="Credits" description="Draft credits are used only when Nudge generates a follow-up with the included AI allowance.">
        <Box sx={{ py: 2.4 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={4}><Typography variant="caption" color="text.secondary">Remaining</Typography><Typography variant="h3" sx={{ fontSize: 30 }}>{creditsRemaining.toLocaleString()}</Typography></Grid>
            <Grid item xs={12} sm={4}><Typography variant="caption" color="text.secondary">Used this month</Typography><Typography variant="h3" sx={{ fontSize: 30 }}>{creditsUsed.toLocaleString()}</Typography></Grid>
            <Grid item xs={12} sm={4}><Typography variant="caption" color="text.secondary">Included + bonus</Typography><Typography variant="h3" sx={{ fontSize: 30 }}>{creditsTotal.toLocaleString()}</Typography></Grid>
          </Grid>
          <LinearProgress variant="determinate" value={creditPct} sx={{ mt: 2.2, height: 8, borderRadius: 99 }} />
        </Box>
      </SectionSurface>
      <ViralShareCard referral={state.referral || {}} region={state.region} />
    </Stack>
  );

  const content = {
    organization: renderOrganization,
    'all-mailboxes': renderAllMailboxes,
    members: renderMembers,
    workspace: renderWorkspace,
    mailboxes: renderMailboxes,
    automation: renderAutomation,
    drafting: renderDrafting,
    safety: renderSafety,
    enrichment: renderEnrichment,
    notifications: renderNotifications,
    credits: renderCredits,
  }[section];

  return (
    <Box className="app-grid" minHeight="100vh">
      <AppHeader />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        <Button color="inherit" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/home')}>Back to dashboard</Button>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-end' }} gap={2} sx={{ mt: 1.5, mb: 3.5 }}>
          <Box>
            <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 46 } }}>Settings</Typography>
            <Typography color="text.secondary" sx={{ mt: .7 }}>{canManageOrganization ? `Manage the organization or configure ${activeWorkspace?.name || 'the active workspace'}.` : `Configure ${activeWorkspace?.name || 'your workspace'}.`}</Typography>
          </Box>
          {saved && <Chip color="success" icon={<CheckCircleOutlineRoundedIcon />} label="Saved" />}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2.2 }}>
          <Select fullWidth size="small" value={section} onChange={(event) => goSection(event.target.value)}>
            {sectionGroups.filter((group) => (!group.requiresAdmin || canManageOrganization) && (!group.requiresOwner || canManageBilling)).flatMap((group) => group.items.map((item) => <MenuItem key={item.id} value={item.id}>{group.label} · {item.label}</MenuItem>))}
          </Select>
        </Box>

        <Grid container spacing={3.2}>
          <Grid item md={2.6} sx={{ display: { xs: 'none', md: 'block' } }}><SettingsNav section={section} onChange={goSection} canManageOrganization={canManageOrganization} canManageBilling={canManageBilling} /></Grid>
          <Grid item xs={12} md={9.4}>{content?.()}</Grid>
        </Grid>
      </Container>
      <UpgradeModal open={Boolean(upgradeReason)} onClose={() => setUpgradeReason('')} reason={upgradeReason} subscription={state.subscription} launchRef={state.pendingReferralCode} onUpgraded={refreshAppState} />
    </Box>
  );
}
